import { DesignerPickerItemResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { DateInput3 } from '@blueprintjs/datetime2';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import DevText from '@app/components/Text';
import { $task } from '@app/containers/pages/Task/store/states';
import { useForm, useRichTextFormField, useTextArrayFormField } from '@app/hooks';
import { getRichStringRequiredValidator, getStringMaxLengthValidator } from '@app/hooks/validation/functions';
import { Button, Callout, Card, Divider, FormGroup, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import RichEditorFormField from '@components/RichEditorFormField';
import { ModalProps } from '@modals/types';

import { createSubTicket, fetchDesignerPickerItems } from '../../store/effects';
import { reconcileSubTicketRef, refreshSubTicketViews, showSubTicketFailure } from '../../store/reconcile';
import { designerPickerKey, nextGeneration } from '../../store/requestGeneration';
import { $designerPicker } from '../../store/states';
import {
    MAX_EXPECTED_DELIVERABLE_LENGTH,
    buildCreateSubTicketPayload,
    resolveDesignerPicker,
    resolveDueWarning,
    SubTicketDueWarning,
} from '../../utils';
import DeliverableListField from '../components/DeliverableListField';
import DesignerPickerField from '../components/DesignerPickerField';

import { submitSubTicketCreate } from './submitCreate';

export interface CreateSubTicketModalProps {
    taskId: ID;
    isArchivedShown: boolean;
}

type Props = ModalProps<CreateSubTicketModalProps>;

const deliverableValidators = [getStringMaxLengthValidator({ maxStringLength: MAX_EXPECTED_DELIVERABLE_LENGTH })];

// Scope is the only source of the server-derived summary that labels the phase everywhere, so
// an empty one would produce an unidentifiable sub-task
const scopeValidators = [getRichStringRequiredValidator({ richStringRequiredMessage: 'Scope is required' })];

const CreateSubTicketModal: React.FC<Props> = ({ closeModal, data }) => {
    const task = useStore($task);
    const designerPicker = useStore($designerPicker);

    // Input 1 of 4 — Scope. There is deliberately no title input: a sub-ticket is labelled by
    // its ordinal plus the summary the server derives from this scope
    const scope = useRichTextFormField({ id: 'scope', validators: scopeValidators, initialValue: '' });

    // Input 2 of 4 — Expected deliverables
    const expectedDeliverables = useTextArrayFormField({
        id: 'expected-deliverables',
        validators: deliverableValidators,
        initialValue: [],
    });

    // Input 3 of 4 — Due date
    const [dueDate, setDueDate] = useState<Date | null>(null);

    // Input 4 of 4 — exactly one designer assignment
    const [selectedDesigner, setSelectedDesigner] = useState<DesignerPickerItemResource | null>(null);

    // A due date in the past, or past the parent Request deadline, is confirmed — never blocked
    const [dueWarning, setDueWarning] = useState<SubTicketDueWarning | null>(null);
    const [isDueWarningConfirmed, setIsDueWarningConfirmed] = useState(false);

    useEffect(() => {
        if (!data) {
            return;
        }

        fetchDesignerPickerItems({
            filter: { task_id: data.taskId },
            generation: nextGeneration(designerPickerKey(data.taskId)),
        }).catch((e) => {
            showSubTicketFailure('The designer list could not be loaded.');
            console.error(e);
        });
    }, [data?.taskId]);

    // Re-picking a date retracts an earlier confirmation, so a warning can never be carried
    // over onto a different date than the one it was shown for
    useEffect(() => {
        setDueWarning(null);
        setIsDueWarningConfirmed(false);
    }, [dueDate]);

    // One gate between the global picker store and this modal. Items held for another Request are
    // withheld outright: offering them would name people who may not be assignable here, and
    // their participation flag would describe the wrong Request.
    const picker = resolveDesignerPicker(designerPicker, data?.taskId ?? '', selectedDesigner);

    const form = useForm({
        fields: [scope, expectedDeliverables],

        apiCall: async () => {
            try {
                // The picker is re-derived rather than trusted: a selection made against another
                // Request's picker stops matching anything on offer here and is dropped, so a
                // create can never be submitted with a designer this Request cannot assign.
                if (!data || !picker.selected) {
                    return { response: false };
                }

                // A phase may be planned without a due date. Null travels to the wire as an
                // explicit `due_at: null` — it is never defaulted to today, and the key is
                // never dropped, because the endpoint requires it
                const dueAt = dueDate ? moment.utc(moment(dueDate).endOf('day').toDate()).format() : null;

                const warning = resolveDueWarning({
                    dueAt,
                    parentDeadlineAt: task?.deadline_at ?? null,
                    nowMs: Date.now(),
                });

                // First submit surfaces the warning and stops; the second proceeds. This is a
                // confirmation step, not a validation failure
                if (warning && !isDueWarningConfirmed) {
                    setDueWarning(warning);
                    return { response: false };
                }

                // The create commits either way. What differs is whether the screen then shows
                // it — and "the screen" is two independent surfaces, the sub-ticket views and
                // the parent Request's Participants card. "Sub-task created." is a claim about
                // both, so `submitSubTicketCreate` only makes it when both are actually current
                // and otherwise gives each stale surface its own retry against its own endpoint.
                await submitSubTicketCreate({
                    taskId: data.taskId,
                    input: buildCreateSubTicketPayload(data.taskId, picker.selected.user.id, {
                        scope: scope.value,
                        expectedDeliverables: expectedDeliverables.value,
                        dueAt,
                    }),
                    scope: {
                        what: 'The sub-task',
                        taskId: data.taskId,
                        refreshViews: () =>
                            refreshSubTicketViews({ taskId: data.taskId, isArchivedShown: data.isArchivedShown }),
                    },
                    deps: {
                        createSubTicket,
                        reconcileRef: reconcileSubTicketRef,
                    },
                });

                closeModal?.();

                return { response: true };
            } catch (e) {
                throw e;
            }
        },
        onFailure: (error) => {
            showSubTicketFailure('The sub-task could not be created. Please try again.');
            console.error(error);
        },
    });

    if (!data) {
        return null;
    }

    // Scope and the assigned designer are the only requirements. The designer has to be one of
    // THIS Request's designers: while the picker is loading or has failed for this Request there
    // is nothing valid to choose, so Create stays disabled rather than submitting a stale or
    // absent assignment. The due date is deliberately absent from this gate.
    const isSubmitDisabled = form.hasFieldErrors || picker.selected === null;

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Create sub-ticket</Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                <form onSubmit={form.handleFormSubmit}>
                    {/* 1 — Scope */}
                    <RichEditorFormField
                        field={scope}
                        formGroupProps={{ label: 'Scope' }}
                        richEditorProps={{ placeholder: 'Describe the scope of this phase' }}
                    />

                    {/* 2 — Expected deliverables */}
                    <DeliverableListField field={expectedDeliverables} />

                    {/* 3 — Due date, optional */}
                    <FormGroup label="Due date" labelInfo="(optional)">
                        <DateInput3
                            fill
                            highlightCurrentDay
                            canClearSelection
                            placeholder="No due date"
                            value={dueDate ? moment(dueDate).format() : null}
                            dayPickerProps={{ firstWeekContainsDate: 1 }}
                            formatDate={(date) => moment(date).format('MMM Do, YYYY (dddd)')}
                            parseDate={(str) => new Date(str)}
                            onChange={(newDate) => setDueDate(newDate ? new Date(newDate) : null)}
                        />
                    </FormGroup>

                    {/* 4 — Designer assignment */}
                    <DesignerPickerField
                        items={picker.items}
                        selected={picker.selected}
                        onSelect={setSelectedDesigner}
                        onClear={() => setSelectedDesigner(null)}
                    />

                    {picker.hasFailed && (
                        <Callout className="mb-2" intent={Intent.DANGER} title="Designers could not be loaded">
                            <DevText>
                                The list of assignable designers is unavailable, so a sub-task cannot be created
                                right now. Reload the page to try again.
                            </DevText>
                        </Callout>
                    )}

                    {dueWarning && (
                        <Callout className="mt-2" intent={Intent.WARNING} title="Check the due date">
                            <DevText>{dueWarning.message}</DevText>
                            <DevText className="mt-1" muted>
                                You can still save it — confirm to continue.
                            </DevText>

                            <Button
                                className="mt-1"
                                intent={Intent.WARNING}
                                disabled={isDueWarningConfirmed}
                                onClick={() => setIsDueWarningConfirmed(true)}
                            >
                                {isDueWarningConfirmed ? 'Confirmed' : 'Confirm due date'}
                            </Button>
                        </Callout>
                    )}

                    <Flex className="mt-2" justify="flex-end">
                        <Button className="mr-1" outlined onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            disabled={isSubmitDisabled}
                            loading={form.isSubmitting}
                            type="submit"
                            intent={Intent.PRIMARY}
                        >
                            Create
                        </Button>
                    </Flex>
                </form>
            </Card>
        </Overlay>
    );
};

export default CreateSubTicketModal;
